import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Trash2, Edit3, ArrowRightCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { Users } from "lucide-react";
import { api } from "@/lib/api";
import { formatEuro, formatDateEU, todayISO } from "@/lib/constants";

const blank = () => ({
  company_name: "",
  category: "Mexican Restaurant",
  contact_person: "",
  phone: "",
  email: "",
  website: "",
  city: "",
  address: "",
  vat_number: "",
  status: "New Lead",
  notes: "",
  last_contact_date: todayISO(),
  next_follow_up_date: "",
  estimated_monthly_potential: 0,
  estimated_monthly_volume: 0,
  products_interested: [],
});

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const [l, m] = await Promise.all([api.listLeads(), api.getMeta()]);
    setLeads(l);
    setMeta(m);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const s = search.toLowerCase();
      const matches =
        !s ||
        l.company_name?.toLowerCase().includes(s) ||
        l.contact_person?.toLowerCase().includes(s) ||
        l.city?.toLowerCase().includes(s);
      const status = statusFilter === "all" || l.status === statusFilter;
      return matches && status;
    });
  }, [leads, search, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };

  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      ...blank(),
      ...lead,
      products_interested: lead.products_interested || [],
      estimated_monthly_potential: lead.estimated_monthly_potential || 0,
      estimated_monthly_volume: lead.estimated_monthly_volume || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.company_name) return toast.error("Company Name is required");
    try {
      if (editing) {
        await api.updateLead(editing.id, form);
        toast.success("Lead updated");
      } else {
        await api.createLead(form);
        toast.success("Lead created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error("Failed to save lead");
    }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete lead "${l.company_name}"?`)) return;
    await api.deleteLead(l.id);
    toast.success("Lead deleted");
    load();
  };

  const convert = async (l) => {
    if (!window.confirm(`Convert "${l.company_name}" to a Customer?`)) return;
    await api.convertLead(l.id);
    toast.success("Converted to customer");
    load();
  };

  const toggleProduct = (p) => {
    setForm((f) => ({
      ...f,
      products_interested: f.products_interested.includes(p)
        ? f.products_interested.filter((x) => x !== p)
        : [...f.products_interested, p],
    }));
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div data-testid="page-leads">
      <PageHeader
        overline="Pipeline"
        title="Leads"
        subtitle="Horeca prospects — restaurants, food trucks, distributors and more."
        actions={
          <div className="flex gap-2">
            <ExportButton module="leads" />
            <Button
              onClick={openCreate}
              data-testid="add-lead-btn"
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white shadow-sm"
            >
              <Plus size={16} className="mr-2" /> New Lead
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact or city"
            className="pl-9 bg-white border-[#E5E5E5]"
            data-testid="lead-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-56 bg-white border-[#E5E5E5]" data-testid="lead-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {meta.lead_statuses.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No leads yet"
          description="Add your first horeca prospect to start tracking deliveries and follow ups."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
              data-testid="empty-add-lead"
            >
              <Plus size={16} className="mr-2" /> Add Lead
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((l) => (
            <div
              key={l.id}
              className="card-soft card-soft-hover p-5 flex flex-col gap-3"
              data-testid={`lead-card-${l.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">{l.category}</div>
                  <h3 className="font-display font-semibold text-lg text-[#222] truncate">{l.company_name}</h3>
                  <div className="text-xs text-[#6C757D]">{l.city || "—"}</div>
                </div>
                <StatusBadge status={l.status} testId={`lead-status-${l.id}`} />
              </div>
              <div className="text-sm text-[#222] grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Contact</div>
                  <div className="truncate">{l.contact_person || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Phone</div>
                  <div className="truncate">{l.phone || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Monthly Potential</div>
                  <div className="font-semibold text-[#E85D04]">{formatEuro(l.estimated_monthly_potential)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Volume</div>
                  <div>{l.estimated_monthly_volume || 0} L/mo</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Next Follow Up</div>
                  <div className="text-[#1F4D3A] font-semibold">{formatDateEU(l.next_follow_up_date)}</div>
                </div>
              </div>
              {l.products_interested?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {l.products_interested.map((p) => (
                    <span
                      key={p}
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-1 flex items-center justify-end gap-1 border-t border-[#E5E5E5] pt-3">
                {l.status !== "Customer" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => convert(l)}
                    className="text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                    data-testid={`convert-lead-${l.id}`}
                  >
                    <ArrowRightCircle size={16} className="mr-1" /> Convert
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(l)}
                  data-testid={`edit-lead-${l.id}`}
                >
                  <Edit3 size={16} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del(l)}
                  className="text-[#E76F51] hover:bg-[#E76F51]/10"
                  data-testid={`delete-lead-${l.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Lead" : "New Lead"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <Field label="Company Name *">
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                data-testid="input-company-name" />
            </Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="input-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.lead_categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                data-testid="input-contact" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="input-phone" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="input-email" />
            </Field>
            <Field label="Website">
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="input-website" />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="input-city" />
            </Field>
            <Field label="Address">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="input-address" />
            </Field>
            <Field label="VAT Number">
              <Input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                placeholder="FI12345678"
                data-testid="input-lead-vat" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="input-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.lead_statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Last Contact Date">
              <Input type="date" value={form.last_contact_date || ""} onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })}
                data-testid="input-last-contact" />
            </Field>
            <Field label="Next Follow Up Date">
              <Input type="date" value={form.next_follow_up_date || ""} onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })}
                data-testid="input-next-followup" />
            </Field>
            <Field label="Estimated Monthly Potential (€)">
              <Input type="number" min="0" value={form.estimated_monthly_potential === 0 ? "" : form.estimated_monthly_potential}
                onChange={(e) => setForm({ ...form, estimated_monthly_potential: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="input-potential" />
            </Field>
            <Field label="Estimated Monthly Volume (L/month)">
              <Input type="number" min="0" value={form.estimated_monthly_volume === 0 ? "" : form.estimated_monthly_volume}
                onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="input-volume" />
            </Field>

            <Field label="Products Interested In" className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-2 mt-1">
                {meta.products.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.products_interested.includes(p)}
                      onCheckedChange={() => toggleProduct(p)}
                      data-testid={`product-${p}`}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </Field>

            <Field label="Notes" className="sm:col-span-2">
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="input-notes" />
            </Field>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-lead">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-lead">
              {editing ? "Save Changes" : "Create Lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
